import React, { useEffect, useState, useCallback } from "react";
import "../../styles/quiz/RealFake.css";
import { useNavigate } from "react-router-dom";
import { fetchBooleanQuizzes } from "../../api/quiz/quizBooleanApi";
import RealFakeArticle from "../../components/quiz/RealFakeArticle";
import RealFakeHeading from "../../components/quiz/RealFakeHeading";
import RealFakeThumbnailNav from "../../components/quiz/RealFakeThumbnailNav";
import RealFakeProgressBar from "../../components/quiz/RealFakeProgressBar";
import { PieChart, Pie, Cell } from "recharts";

// Main quiz component for Real or Fake questions
const QuizPage = () => {
  // State variables
  const [quizzes, setQuizzes] = useState([]); // Loaded quiz data
  const [currentIndex, setCurrentIndex] = useState(0); // Current question index
  const [showResult, setShowResult] = useState(false); // Whether to show result screen
  const [userAnswers, setUserAnswers] = useState([]); // User's answers
  const [showTip, setShowTip] = useState(false); // Toggle for hint/tip display
  const [selectedDetail, setSelectedDetail] = useState(null); // Expanded feedback detail
  const [warning, setWarning] = useState(""); // Warning if user submits early
  const navigate = useNavigate();
  const [showUnansweredHighlights, setShowUnansweredHighlights] =
    useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primary-bg", "#ebebe1");
    root.style.setProperty("--box-bg", "#3e5e48");
    root.style.setProperty("--accent-yellow", "#fbc640");
    root.style.setProperty("--main-green", "#2f472c");
  }, []);

  // Load quiz data on mount
  useEffect(() => {
    fetchBooleanQuizzes().then(setQuizzes);
  }, []);

  const handleAnswer = useCallback(
    (userAnswer) => {
      const currentQuiz = quizzes[currentIndex];
  
      // 防止 currentQuiz 为空或 correct_answer 缺失导致程序崩溃
      if (!currentQuiz || typeof currentQuiz.correct_answer === "undefined") {
        console.warn("Quiz data is missing or incomplete at index:", currentIndex);
        return;
      }
  
      const isCorrect = userAnswer === currentQuiz.correct_answer;
  
      const updatedAnswers = [...userAnswers];
      updatedAnswers[currentIndex] = {
        ...currentQuiz,
        userAnswer,
        isCorrect,
      };
  
      setUserAnswers(updatedAnswers);
      setShowTip(false);
      setWarning("");
  
      const allAnsweredNow = quizzes.every(
        (_, idx) => updatedAnswers[idx] !== undefined
      );
  
      if (allAnsweredNow && !showResult) {
        const confirmed = window.confirm(
          "You finished all the quizzes, do you want to submit the quiz and see the result?"
        );
        if (confirmed) {
          setShowResult(true);
        }
      }
  
      if (currentIndex < quizzes.length - 1) {
        setTimeout(() => setCurrentIndex((prev) => prev + 1), 200);
      }
    },
    [quizzes, currentIndex, userAnswers, showResult]
  );
  

  // Navigate to previous/next question manually
  // const handlePrev = () => {
  //   if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
  //   setShowTip(false);
  // };
  // const handleNext = () => {
  //   if (currentIndex < quizzes.length - 1) setCurrentIndex((prev) => prev + 1);
  //   setShowTip(false);
  // };

  // Navigate by clicking thumbnails
  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
    setShowTip(false);
  };

  // Handle submission and show results
  const handleFinish = () => {
    if (
      userAnswers.length !== quizzes.length ||
      userAnswers.includes(undefined)
    ) {
      setWarning("Please answer all questions before finishing the quiz.");
      setShowUnansweredHighlights(true);
    } else {
      setShowResult(true);
      setWarning("");
      setShowUnansweredHighlights(false);
    }
  };

  // Reset the quiz to try again
  const resetQuiz = () => {
    setCurrentIndex(0);
    setShowResult(false);
    setUserAnswers([]);
    setShowTip(false);
    setSelectedDetail(null);
    setWarning("");
  };

  // Show loading spinner while quizzes load
  if (quizzes.length === 0) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        Loading quiz...
      </div>
    );
  }

  // Score calculation
  const quiz = quizzes[currentIndex];
  const correctCount = userAnswers.filter((a) => a?.isCorrect).length;
  const wrongCount = quizzes.length - correctCount;
  const percent = (correctCount / quizzes.length) * 100;

  // Pie chart data
  const pieData = [
    { name: "Correct", value: correctCount },
    { name: "Wrong", value: wrongCount },
  ];
  const COLORS = ["#fbc640", "#5f6f56"];

  // Score-based feedback message
  const getFeedbackMessage = (score) => {
    if (score >= 9) {
      return {
        tier: "🎉 Expert Voter",
        message:
          "Amazing! You’re an expert at separating fact from fiction. Keep up your critical thinking!",
      };
    } else if (score >= 6) {
      return {
        tier: "👍 Informed Voter",
        message:
          "Well done! You're well-informed, with just a few tricky claims. Stay sharp and keep questioning.",
      };
    } else if (score >= 3) {
      return {
        tier: "🤔 Learning Voter",
        message:
          "You're on your way! Keep practicing your fact-checking skills and review the explanations carefully.",
      };
    } else {
      return {
        tier: "⚠️ Misinformed Risk",
        message:
          "It’s a tough start, but don't worry! Strengthen your media literacy by reviewing the explanations and trying again.",
      };
    }
  };

  return (
    <div className="quiz-wrapper text-center">
      {!showResult ? (
        <>
          <div className="quiz-layout">
            <RealFakeHeading />

            <RealFakeThumbnailNav
              quizzes={quizzes}
              currentIndex={currentIndex}
              userAnswers={userAnswers}
              showUnansweredHighlights={showUnansweredHighlights}
              onThumbnailClick={handleThumbnailClick}
            />

            <div className="quiz-content">
              <RealFakeProgressBar
                currentIndex={currentIndex}
                total={quizzes.length}
              />

              <div className="quiz-article-navigation">
                <RealFakeArticle
                  quiz={quiz}
                  showTip={showTip}
                  setShowTip={setShowTip}
                />
              </div>

              <div className="answer-buttons mt-4">
                <button
                  className={`btn real-button ${
                    userAnswers[currentIndex]?.userAnswer === 1
                      ? "selected-answer"
                      : ""
                  }`}
                  onClick={() => handleAnswer(1)}
                >
                  REAL
                </button>
                <button
                  className={`btn fake-button ${
                    userAnswers[currentIndex]?.userAnswer === 0
                      ? "selected-answer"
                      : ""
                  }`}
                  onClick={() => handleAnswer(0)}
                >
                  FAKE
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5 button-row">
            <button
              className="btn btn-warning"
              onClick={() => navigate("/challenge")}
            >
              Back to Challenges
            </button>
            <button className="btn btn-warning" onClick={handleFinish}>
              Submit Quiz
            </button>
          </div>

          {warning && <p className="warning-text mt-2">{warning}</p>}
        </>
      ) : (
        <>
          <h4 className="quiz-performance-heading">
            REAL OR FAKE QUIZ PERFORMANCE
          </h4>

          <div className="performance-summary-box">
            <div className="performance-text">
              {(() => {
                const { tier, message } = getFeedbackMessage(correctCount);
                return (
                  <>
                    <h5>{tier}</h5>
                    <p>{message}</p>
                  </>
                );
              })()}
              <p>
                Score {correctCount}/{quizzes.length}
              </p>
            </div>

            <div className="pie-chart-container">
              <PieChart width={160} height={160}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
              <div className="pie-center-label">{Math.round(percent)}%</div>
            </div>
          </div>

          <div className="answer-bar-list">
            {userAnswers.map((ans, i) => (
              <React.Fragment key={i}>
                <div
                  className="answer-bar-item"
                  onClick={() =>
                    setSelectedDetail(selectedDetail === i ? null : i)
                  }
                >
                  <div className="thumbnail-wrapper">
                    <img
                      src={ans.image_url}
                      alt={ans.headline}
                      className="thumbnail-img-small"
                    />
                    <div
                      className={`badge-overlay ${
                        ans.correct_answer === 1 ? "badge-real" : "badge-fake"
                      }`}
                    >
                      {ans.correct_answer === 1 ? "REAL" : "FAKE"}
                    </div>
                  </div>
                  <div className="bar-item-text">
                    {ans.headline}
                    <div
                      className={`result-status ${
                        ans.isCorrect ? "correct" : "wrong"
                      }`}
                    >
                      Your answer was {ans.isCorrect ? "Correct" : "Wrong"}
                    </div>
                    <div className="feedback-text-bar">📚 {ans.feedback}</div>
                  </div>
                </div>

                {selectedDetail === i && (
                  <div className="quiz-box newspaper-style-box mt-3">
                    <div
                      className="news-header"
                      style={{
                        color: ans.correct_answer === 1 ? "#2f8246" : "#c73232",
                      }}
                    >
                      {ans.correct_answer === 1 ? "REAL NEWS" : "FAKE NEWS"}
                    </div>
                    <hr className="news-line" />
                    <h2 className="headline">{ans.headline}</h2>
                    <hr className="news-line" />
                    <div className="news-inner-layout">
                      <div className="news-text">
                        <p className="summary">{ans.summary}</p>
                        <div className="tip-row">
                          <span
                            className="tip-toggle"
                            onClick={() => setShowTip((prev) => !prev)}
                          >
                            💡
                          </span>
                          {showTip && (
                            <span className="inline-tip">{ans.tip}</span>
                          )}
                        </div>
                      </div>
                      <div className="news-image">
                        <img
                          src={ans.image_url}
                          alt="quiz"
                          className="quiz-image-remote"
                        />
                        <div className="photo-credit">
                          Photo by Wikimedia Commons
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="result-buttons-wrapper">
            <button
              className="btn btn-warning result-button"
              onClick={resetQuiz}
            >
              TRY AGAIN
            </button>
            <button
              className="btn btn-warning result-button"
              onClick={() => navigate("/challenge")}
            >
              BACK TO CHALLENGES
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuizPage;
